// Certificado de inocuidad público (sin login) — cadena de frío de un despacho.
// Ruta /cadena/:despachoId. Prueba verificable para el cliente.
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert, Loader2, Thermometer } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

interface Lectura { hora: string | null; momento: string; tipo: string; temp_c: number; origen: string; en_banda: boolean }
interface Cert { codigo: string; fecha: string; ruta: string | null; placa: string | null; lecturas: Lectura[]; n_lecturas: number; violaciones: number; temp_min: number | null; temp_max: number | null; veredicto: string; config: { caliente_min_c: number; frio_max_c: number } }
interface Resp { valido: boolean; certificado?: Cert }
const MOM: Record<string, string> = { produccion: 'Producción', carga: 'Carga', transito: 'Tránsito', entrega: 'Entrega' };
const hhmm = (iso: string | null) => iso ? new Date(iso).toLocaleString(LOCALE, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

export function CadenaCert() {
  const { despachoId = '' } = useParams();
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ['cadena-cert', despachoId],
    queryFn: async () => (await apiClient.post<Resp>('/rpc/cadena_cert_publico', { _despacho_id: despachoId })).data,
  });
  if (isLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data?.valido || !data.certificado) return <div className="flex min-h-screen items-center justify-center bg-slate-900 text-white">Certificado no encontrado</div>;
  const c = data.certificado;
  const ok = c.veredicto === 'integra';
  return (
    <div className="min-h-screen bg-slate-50 p-4 dark:bg-slate-900">
      <div className="mx-auto max-w-2xl space-y-4">
        <div className={`flex items-center gap-4 rounded-xl border-2 p-4 ${ok ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-red-500 bg-red-50 dark:bg-red-900/20'}`}>
          {ok ? <ShieldCheck className="h-12 w-12 text-emerald-600" /> : <ShieldAlert className="h-12 w-12 text-red-600" />}
          <div>
            <p className="text-xs uppercase text-muted-foreground">Certificado de cadena de frío</p>
            <p className={`text-xl font-bold ${ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>{ok ? 'Cadena íntegra' : 'Cadena comprometida'}</p>
            <p className="text-sm text-muted-foreground">{c.codigo} · {c.fecha} · {c.ruta ?? ''}{c.placa ? ` · ${c.placa}` : ''}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg border bg-white p-3 dark:bg-slate-800"><p className="text-xs text-muted-foreground">Lecturas</p><p className="text-lg font-semibold">{c.n_lecturas}</p></div>
          <div className="rounded-lg border bg-white p-3 dark:bg-slate-800"><p className="text-xs text-muted-foreground">Fuera de banda</p><p className={`text-lg font-semibold ${c.violaciones > 0 ? 'text-red-600' : ''}`}>{c.violaciones}</p></div>
          <div className="rounded-lg border bg-white p-3 dark:bg-slate-800"><p className="text-xs text-muted-foreground">Rango °C</p><p className="text-lg font-semibold">{c.temp_min ?? '—'}–{c.temp_max ?? '—'}</p></div>
        </div>
        <div className="rounded-xl border bg-white p-4 dark:bg-slate-800">
          <p className="mb-2 flex items-center gap-2 text-sm font-medium"><Thermometer className="h-4 w-4" /> Línea de tiempo · banda caliente ≥ {c.config.caliente_min_c}°C / frío ≤ {c.config.frio_max_c}°C</p>
          <table className="w-full text-sm">
            <tbody>
              {c.lecturas.map((l, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1.5 pr-2 whitespace-nowrap text-muted-foreground">{hhmm(l.hora)}</td>
                  <td className="py-1.5 pr-2">{MOM[l.momento] ?? l.momento}</td>
                  <td className={`py-1.5 pr-2 text-right font-mono ${!l.en_banda ? 'font-semibold text-red-600' : ''}`}>{l.temp_c}°C</td>
                  <td className="py-1.5 pl-2 text-right">{l.en_banda ? '✓' : '✗'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-center text-xs text-muted-foreground">Documento verificable emitido por el sistema de catering.</p>
      </div>
    </div>
  );
}
